import {useMemo} from 'react'

import type {File} from '../components/ContentPreview/content-preview-types'

function prettySize(bytes: number) {
  if (bytes === 0) return '0 bytes'
  if (bytes === 1) return '1 byte'

  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`
}

const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  md: 'markdown',
  markdown: 'markdown',
  html: 'html',
  htm: 'html',
  mmd: 'mermaid',
}

export function getEffectiveLanguage(file: File): string {
  if (file.language) return file.language.toLowerCase()
  const dotIndex = file.name.lastIndexOf('.')
  const ext = dotIndex >= 0 ? file.name.slice(dotIndex + 1).toLowerCase() : ''
  return EXTENSION_TO_LANGUAGE[ext] ?? ''
}

export function useFileInfo(file: File) {
  return useMemo(() => {
    const bytes = new Blob([file.value]).size
    const previewableLanguages = new Set<string>(['markdown', 'html', 'mermaid'])
    const effectiveLanguage = getEffectiveLanguage(file)

    return {
      linesOfCode: file.value.split('\n').length,
      isPreviewable: previewableLanguages.has(effectiveLanguage),
      size: prettySize(bytes),
    }
  }, [file])
}

/**
 * Extracts repository owner, repository name, and file path from a GitHub URL.
 * Validates URL security by checking:
 * - URL is from the current GitHub instance
 * @param url - GitHub URL (e.g., https://github.com/owner/repo/blob/sha/path/to/file.md)
 * @returns Object with repoOwner, repoName, and filePath, or null if parsing fails or URL is unsafe
 */
export function parseGitHubUrl(url: string): {repoOwner: string; repoName: string; filePath: string} | null {
  try {
    const urlObj = new URL(url, window.location.origin)

    // Security validation: Only allow URLs from the current GitHub instance
    // This automatically supports github.com and EMU instances
    if (urlObj.origin !== window.location.origin) {
      return null
    }

    const pathParts = urlObj.pathname.split('/').filter(Boolean)

    // Expected format: /owner/repo/blob|tree/ref/path/to/file
    if (pathParts.length < 4) {
      return null
    }

    const repoOwner = pathParts[0]
    const repoName = pathParts[1]

    if (!repoOwner || !repoName) {
      return null
    }

    // Skip 'blob' or 'tree' and the ref (commit sha/branch), then join the rest as file path
    const filePath = pathParts.slice(4).join('/')

    return {repoOwner, repoName, filePath}
  } catch {
    return null
  }
}

/**
 * Formats a file path with repository information in a readable format.
 * @param repoOwner - The repository owner
 * @param repoName - The repository name
 * @param filePath - The file path within the repository
 * @param ref - Optional branch or commit ref to include (e.g., "main")
 * @returns Formatted path string (e.g., "owner / repo / main / path / to / file.md")
 */
export function formatFilePath(repoOwner: string, repoName: string, filePath: string, ref?: string): string {
  const repoPrefix = ref ? `${repoOwner} / ${repoName} / ${ref}` : `${repoOwner} / ${repoName}`
  return `${repoPrefix} / ${filePath.split('/').join(' / ')}`
}
